import { useEffect, useRef, useState } from 'react';
import { chatApi, chatInitialState, sendToWs, useGetChatConnectionQuery } from '../api/chatApi';
import { ChatMessageTypes } from '../api/types';
import { DialogCard } from '../components/DialogCard';
import { Link, NavLink, useParams } from 'react-router-dom';
import { Input } from '../../../components/Input';
import { PaperAirplaneIcon, XMarkIcon } from '@heroicons/react/16/solid';
import { Bubble } from '../components/Bubble';
import { TypingDots } from '../components/TypingDots';
import { useAppDispatch } from '../../../store';
import { getFormattedChatDate } from '../../../utils/dates';
import { useSelector } from 'react-redux';
import { authSelector } from '../../auth/state/authSlice';

export const Chat = () => {
  const dispatch = useAppDispatch();
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const [text, setText] = useState('');
  const [scrollPosition, setScrollPosition] = useState(0);
  const { token } = useSelector(authSelector);
  const { data } = useGetChatConnectionQuery(undefined, { skip: !token });
  const { dialogId = '' } = useParams();
  const { conversation, messages, isTyping } = data || chatInitialState;

  const isDialogSelected = Boolean(dialogId);

  useEffect(() => {
    sendToWs({ msg_type: ChatMessageTypes.GetDialogs });
  }, [data?.isConnected]);

  useEffect(() => {
    if (dialogId) {
      sendToWs({
        msg_type: ChatMessageTypes.GetMessagesFromDialog,
        dialog_id: dialogId,
        page: '0'
      });
    } else {
      dispatch(
        chatApi.util.updateQueryData('getChatConnection', undefined, (draft) => {
          draft.conversation = null;
          draft.messages = [];
          draft.nextPage = null;
        })
      );
    }
  }, [dialogId, data?.isConnected]);

  useEffect(() => {
    const timer = setTimeout(() => {
      sendToWs({
        msg_type: ChatMessageTypes.TypingStopped,
        recipient: String(conversation?.recipient.id)
      });
    }, 800);

    return () => {
      clearTimeout(timer);
      if (!text) return;
      sendToWs({
        msg_type: ChatMessageTypes.IsTyping,
        recipient: String(conversation?.recipient.id)
      });
    };
  }, [text]);

  useEffect(() => {
    if (messages?.length > 0) {
      const markAllMessagesHasRead = () => {
        for (const message of messages) {
          if (!message.is_me && !message.read) {
            sendToWs({
              msg_type: ChatMessageTypes.MessageRead,
              msg_id: String(message.id),
              dialog_id: dialogId
            });
          }
        }
      };
      markAllMessagesHasRead();
      const pos = chatContainerRef.current?.scrollHeight;
      setScrollPosition(pos || 0);
    }
  }, [messages, dialogId]);

  const onSendMessage = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!text) return;
    sendToWs({ msg_type: ChatMessageTypes.TextMessage, dialog_id: dialogId, text });
    setText('');
  };

  useEffect(() => {
    chatContainerRef.current?.scrollTo({
      top: scrollPosition,
      behavior: 'smooth'
    });
  }, [scrollPosition, isTyping]);

  const currentMessages = [...messages].reverse();

  const hasNoConversations = data && data.conversations.length === 0;

  if (hasNoConversations) {
    return (
      <div className="text-center flex flex-col gap-4 min-h-[90vh] w-full justify-center items-center">
        <p className="text-cyan-800 text-2xl sm:text-3xl font-semibold">
          You don't have any conversations yet.
        </p>
        <p className="text-cyan-800 text-xl sm:text-3xl mb-8">Start chatting with other readers!</p>
        <NavLink
          to="/books"
          className="text-white font-bold text-lg sm:text-xl p-2 px-4 bg-cyan-800 rounded-lg">
          Explore
        </NavLink>
      </div>
    );
  }

  return (
    <div className="flex gap-2 min-h-[90vh] w-full">
      <div className="w-full sm:w-[30rem] border border-cyan-800 rounded-lg">
        <p className="p-3 font-semibold text-center border-b border-cyan-700">Conversations</p>
        {data?.conversations.map((conversation) => (
          <DialogCard key={conversation.id} dialog={conversation} />
        ))}
      </div>
      {/* chat window in laptop */}
      <div className="w-full border border-cyan-800 rounded-lg hidden sm:flex flex-col">
        <p className="p-3 font-semibold border-b border-cyan-700">
          Chat {conversation && 'with ' + conversation.recipient.full_name}
        </p>
        <div
          ref={chatContainerRef}
          className="flex flex-col gap-2 overflow-y-auto h-[78vh] p-3 relative">
          {!conversation && (
            <div className="text-center text-cyan-500 h-full w-full flex items-center justify-center">
              <p className="font-semibold text-2xl">Select a conversation to start chatting</p>
            </div>
          )}
          {currentMessages.map((message, index) => {
            const { created } = message;
            const previousMessage = currentMessages?.[index - 1];
            const date = getFormattedChatDate(new Date(created));
            const previousDate =
              previousMessage?.created && getFormattedChatDate(new Date(previousMessage.created));

            return (
              <div key={message.id} className="flex flex-col gap-1">
                {previousDate !== date && (
                  <div className="text-center text-xs text-cyan-500">
                    <p>{date}</p>
                  </div>
                )}
                <Bubble message={message} />
              </div>
            );
          })}
          {data?.isTyping && (
            <div className="py-4">
              <TypingDots />
            </div>
          )}
        </div>
        {isDialogSelected && (
          <div className="w-full border-t border-cyan-700 p-3">
            <form className="flex gap-2 w-full" onSubmit={onSendMessage}>
              <Input
                placeholder="Type a message"
                value={text}
                onChange={(e) => setText(e.target.value)}
              />
              <button
                type="submit"
                className="w-10 h-10 flex items-center justify-center cursor-pointer p-2 rounded-full bg-cyan-800 hover:bg-cyan-700 transition duration-200 transform -rotate-45">
                <PaperAirplaneIcon className="size-5 text-white" />
              </button>
            </form>
          </div>
        )}
      </div>
      {/* chat window in mobile */}
      {isDialogSelected && (
        <div className="flex sm:hidden fixed top-2 h-[40rem] left-2 right-2 border z-50">
          <div className="w-full border-t flex flex-col gap-2 justify-between border-cyan-700 p-2 bg-white rounded-lg">
            <div className="flex justify-between items-center border-b border-cyan-700">
              <p className="p-1 font-semibold  text-sm">
                Chat {conversation && 'with ' + conversation.recipient.full_name}
              </p>
              <Link to="/chat" className='p-1'>
                <XMarkIcon className="w-6 h-6 cursor-pointer" />
              </Link>
            </div>
            <div
              ref={chatContainerRef}
              className="flex flex-col gap-2 overflow-y-auto h-[78vh] relative grow">
              {!conversation && (
                <div className="text-center text-cyan-500 h-full w-full flex items-center justify-center">
                  <p className="font-semibold text-2xl">Select a conversation to start chatting</p>
                </div>
              )}
              {currentMessages.map((message, index) => {
                const { created } = message;
                const previousMessage = currentMessages?.[index - 1];
                const date = getFormattedChatDate(new Date(created));
                const previousDate =
                  previousMessage?.created &&
                  getFormattedChatDate(new Date(previousMessage.created));

                return (
                  <div key={message.id} className="flex flex-col gap-1">
                    {previousDate !== date && (
                      <div className="text-center text-xs text-cyan-500">
                        <p>{date}</p>
                      </div>
                    )}
                    <Bubble message={message} />
                  </div>
                );
              })}
              {data?.isTyping && (
                <div className="py-4">
                  <TypingDots />
                </div>
              )}
            </div>
            <form className="flex gap-2 w-full" onSubmit={onSendMessage}>
              <Input
                placeholder="Type a message"
                value={text}
                onChange={(e) => setText(e.target.value)}
              />
              <button
                type="submit"
                className="w-10 h-10 flex items-center justify-center cursor-pointer p-2 rounded-full bg-cyan-800 hover:bg-cyan-700 transition duration-200 transform -rotate-45">
                <PaperAirplaneIcon className="size-5 text-white" />
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
